// Invoices module — create customer-facing invoices (XLSX backup + PDF)
// from carrier-billed shipments.

import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Card } from '../components/Card'
import { Icon } from '../components/Icon'
import { DateRangePicker } from '../components/DateRangePicker'
import { useTranslation } from 'react-i18next'
import { CopyCell } from '../components/CopyCell'
import { DownloadLink } from '../components/DownloadLink'
import { RecordHistory } from '../components/RecordHistory'
import { useAuth } from '../lib/auth'

interface CustomerOption {
  customer_name: string
  shipment_count: number
}

interface GenerateResult {
  ok?: boolean
  invoice_number?: string
  customer_name?: string
  period_start?: string
  period_end?: string
  carrier_filter?: string | null
  subtotal?: number
  tax?: number
  total?: number
  new_shipment_count?: number
  adjustment_count?: number
  carrier_totals?: Record<string, number>
  xlsx_path?: string
  pdf_path?: string
  marked_invoiced?: boolean
  error?: string
  tail?: string
  traceback?: string
  exit_code?: number
}

interface InvoiceListRow {
  id: number
  invoice_number: string
  customer_name: string
  period_start: string
  period_end: string
  invoice_date: string | null
  carrier_filter: string | null
  subtotal: number
  tax: number
  total: number
  new_shipment_count: number
  adjustment_count: number
  xlsx_path: string | null
  pdf_path: string | null
  created_at: string
  marked_invoiced: boolean
  items_recorded?: boolean   // false = generated before contents were recorded (cannot be finalised)
}

interface AdjustmentLine {
  rebill_id: number
  shipment_number: string
  carrier: string
  adjustment_category: string
  adjustment_name: string
  cost_amount: string
  markup_pct: string
  rebill_amount: string
  rebill_reason: string | null
  carrier_invoice_date: string
}

const CARRIERS: { code: string; label: string }[] = [
  { code: '',         label: 'All carriers' },
  { code: 'usps',     label: 'USPS' },
  { code: 'dhl',      label: 'DHL' },
  { code: 'fedex',    label: 'FedEx' },
  { code: 'ups',      label: 'UPS' },
  { code: 'asendia',  label: 'Asendia' },
  { code: 'evri',     label: 'Evri' },
  { code: 'epost',    label: 'EPost' },
  { code: 'intelcom', label: 'Intelcom' },
  { code: 'uniuni',   label: 'UniUni' },
]

function fmtUsd(n: number | undefined | null): string {
  if (n == null || isNaN(n as number)) return '$0.00'
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10)
}
function monthAgoIso(): string {
  const d = new Date()
  d.setDate(d.getDate() - 30)
  return d.toISOString().slice(0, 10)
}
/** Add one day to an ISO YYYY-MM-DD date. Used to convert the picker's
 *  INCLUSIVE end-date into the API's EXCLUSIVE period_end. */
function addOneDay(isoDate: string): string {
  const [y, m, d] = isoDate.split('-').map(Number)
  const date = new Date(y, m - 1, d + 1)
  const yy = date.getFullYear()
  const mm = String(date.getMonth() + 1).padStart(2, '0')
  const dd = String(date.getDate()).padStart(2, '0')
  return `${yy}-${mm}-${dd}`
}

/**
 * Programmatically download a same-origin /api file WITH auth.
 *
 * Must go through fetch(), NOT <a href>. A plain anchor navigation does not
 * pass through the global fetch-token patch (lib/api-fetch.ts), so it reaches
 * the API with no Authorization header; the auth gate answers 401 with a JSON
 * body, and the browser saves that as "xlsx.json" / "pdf.json". Fetching gets
 * the token attached and yields the real file. Same approach as DownloadLink,
 * but callable imperatively (used for the auto-download after generation).
 */
async function triggerDownload(url: string) {
  let objectUrl: string | null = null
  try {
    const res = await fetch(url)   // token added by the api-fetch patch
    if (!res.ok) {
      console.error('auto-download failed', url, res.status)
      return
    }
    const blob = await res.blob()
    objectUrl = URL.createObjectURL(blob)

    // Prefer the server's filename (SEP260001.xlsx); fall back to the URL tail.
    const disposition = res.headers.get('Content-Disposition') ?? ''
    const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition)
    const filename = match ? decodeURIComponent(match[1]) : (url.split('/').pop() || 'download')

    const a = document.createElement('a')
    a.href = objectUrl
    a.download = filename
    document.body.appendChild(a)
    a.click()
    a.remove()
  } catch (e) {
    console.error('auto-download error', url, e)
  } finally {
    // Defer revoke so the browser has time to start the save.
    if (objectUrl) setTimeout(() => URL.revokeObjectURL(objectUrl!), 10_000)
  }
}

function InvoiceDetailModal({
  inv,
  onClose,
}: {
  inv: InvoiceListRow
  onClose: () => void
}) {
  const adjQuery = useQuery<{ adjustments: AdjustmentLine[] }>({
    queryKey: ['invoices', 'adjustments', inv.invoice_number],
    queryFn: () =>
      fetch(`/api/invoices/${inv.invoice_number}/adjustments`).then(r => r.json()),
    staleTime: 60_000,
  })

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-ink-100">
          <div>
            <div className="text-[16px] font-semibold">
              Invoice {inv.invoice_number}
            </div>
            <div className="text-[12px] text-ink-500 mt-0.5">
              {inv.customer_name} · {inv.period_start} → {inv.period_end}
              {inv.carrier_filter ? ` · ${inv.carrier_filter.toUpperCase()}` : ''}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-[15px] font-semibold num">{fmtUsd(inv.total)}</span>
            <DownloadLink
              href={`/api/invoices/${inv.invoice_number}/xlsx`}
              download
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg bg-mint-50 text-mint-700 text-[12.5px] font-semibold hover:bg-mint-100"
            >
              <Icon name="file-spreadsheet" className="w-3.5 h-3.5" /> XLSX
            </DownloadLink>
            <DownloadLink
              href={`/api/invoices/${inv.invoice_number}/pdf`}
              download
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg bg-royal-50 text-royal-700 text-[12.5px] font-semibold hover:bg-royal-100"
            >
              <Icon name="file-text" className="w-3.5 h-3.5" /> PDF
            </DownloadLink>
            <button
              onClick={onClose}
              className="h-8 w-8 rounded-lg flex items-center justify-center hover:bg-ink-100"
            >
              <Icon name="x" className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Summary bar */}
        <div className="flex items-center gap-6 px-5 py-3 bg-ink-50 border-b border-ink-100 text-[12.5px]">
          <span className="text-ink-500">Shipments: <span className="font-semibold text-ink-700">{inv.new_shipment_count}</span></span>
          <span className="text-ink-500">Adjustments: <span className="font-semibold text-ink-700">{inv.adjustment_count}</span></span>
          <span className="text-ink-500">Subtotal: <span className="font-semibold text-ink-700 num">{fmtUsd(inv.subtotal)}</span></span>
          <span className="text-ink-500">Tax: <span className="font-semibold text-ink-700 num">{fmtUsd(inv.tax)}</span></span>
          <span className="text-ink-500">Total: <span className="font-semibold text-ink-700 num">{fmtUsd(inv.total)}</span></span>
          <span className="ml-auto">
            {inv.marked_invoiced
              ? <span className="pill pill-mint">INVOICED</span>
              : <span className="pill pill-ink">DRAFT</span>}
          </span>
        </div>

        {/* Adjustments table */}
        <div className="flex-1 overflow-auto px-5 py-4">
          <div className="text-[13px] font-semibold mb-2">
            Adjustment Detail (Internal)
          </div>
          {adjQuery.isLoading && (
            <div className="text-[13px] text-ink-500">Loading adjustments…</div>
          )}
          {adjQuery.error && (
            <div className="text-[13px] text-flame-600">Failed to load adjustments.</div>
          )}
          {adjQuery.data && adjQuery.data.adjustments.length === 0 && (
            <div className="text-[13px] text-ink-500">No adjustments on this invoice.</div>
          )}
          {adjQuery.data && adjQuery.data.adjustments.length > 0 && (
            <table className="w-full text-[12px]">
              <thead className="text-[10.5px] uppercase tracking-wider text-ink-500">
                <tr className="border-b border-ink-100">
                  <th className="text-left py-1.5 pr-2">Carrier</th>
                  <th className="text-left py-1.5 pr-2">Tracking #</th>
                  <th className="text-left py-1.5 pr-2">Date</th>
                  <th className="text-left py-1.5 pr-2">Category</th>
                  <th className="text-left py-1.5 pr-2">Adjustment</th>
                  <th className="text-right py-1.5 pr-2">Cost to BL</th>
                  <th className="text-right py-1.5 pr-2">Mkup %</th>
                  <th className="text-right py-1.5">Billed</th>
                </tr>
              </thead>
              <tbody>
                {adjQuery.data.adjustments.map((a) => (
                  <tr key={a.rebill_id} className="border-b border-ink-50 hover:bg-ink-50">
                    <td className="py-1.5 pr-2">
                      <span className="pill pill-ink">{a.carrier.toUpperCase()}</span>
                    </td>
                    <td className="py-1.5 pr-2"><CopyCell value={a.shipment_number} /></td>
                    <td className="py-1.5 pr-2 num text-ink-500">{a.carrier_invoice_date?.slice(0, 10)}</td>
                    <td className="py-1.5 pr-2 text-ink-600">{a.adjustment_category}</td>
                    <td className="py-1.5 pr-2 text-ink-700">{a.adjustment_name}</td>
                    <td className="py-1.5 pr-2 text-right num">{fmtUsd(parseFloat(a.cost_amount))}</td>
                    <td className="py-1.5 pr-2 text-right num">{parseFloat(a.markup_pct).toFixed(1)}%</td>
                    <td className="py-1.5 text-right num font-semibold">{fmtUsd(parseFloat(a.rebill_amount))}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-ink-200">
                  <td colSpan={7} className="py-2 text-right font-semibold text-[12px] pr-2">Total billed:</td>
                  <td className="py-2 text-right num font-semibold text-[12px]">
                    {fmtUsd(adjQuery.data.adjustments.reduce((s, a) => s + parseFloat(a.rebill_amount), 0))}
                  </td>
                </tr>
              </tfoot>
            </table>
          )}
          {adjQuery.data && adjQuery.data.adjustments.some(a => a.rebill_reason) && (
            <div className="mt-4">
              <div className="text-[12px] font-semibold mb-1 text-ink-600">Reasons</div>
              {adjQuery.data.adjustments.filter(a => a.rebill_reason).map(a => (
                <div key={a.rebill_id} className="text-[11.5px] text-ink-500 py-0.5 flex gap-2">
                  <CopyCell value={a.shipment_number} />
                  <span>{a.rebill_reason}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function VoidConfirmModal({
  inv,
  onConfirm,
  onCancel,
  isPending,
}: {
  inv: InvoiceListRow
  onConfirm: () => void
  onCancel: () => void
  isPending: boolean
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={(e) => { if (e.target === e.currentTarget) onCancel() }}
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 p-6">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-flame-50 flex items-center justify-center shrink-0">
            <Icon name="alert-triangle" className="w-5 h-5 text-flame-600" />
          </div>
          <div>
            <div className="text-[15px] font-semibold">Void invoice {inv.invoice_number}?</div>
            <div className="text-[13px] text-ink-500 mt-1">
              This will reset all carrier rows back to <span className="font-semibold">PENDING</span> and
              delete this invoice record. The action cannot be undone from the UI.
            </div>
          </div>
        </div>
        <div className="bg-ink-50 rounded-lg px-4 py-3 text-[12.5px] text-ink-600 mb-5 space-y-0.5">
          <div><span className="font-semibold">Customer:</span> {inv.customer_name}</div>
          <div><span className="font-semibold">Period:</span> {inv.period_start} → {inv.period_end}</div>
          <div><span className="font-semibold">Total:</span> {fmtUsd(inv.total)} · {inv.new_shipment_count} shipments</div>
          {inv.marked_invoiced && (
            <div className="text-flame-600 font-semibold mt-1">⚠ Carrier rows are marked INVOICED — they will be reset to PENDING.</div>
          )}
        </div>
        <div className="flex items-center justify-end gap-3">
          <button
            onClick={onCancel}
            disabled={isPending}
            className="h-9 px-4 rounded-lg border border-ink-200 text-[13px] font-semibold hover:bg-ink-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isPending}
            className="h-9 px-4 rounded-lg bg-flame-600 text-white text-[13px] font-semibold hover:bg-flame-700 disabled:opacity-50 inline-flex items-center gap-2"
          >
            {isPending
              ? <><Icon name="loader" className="w-4 h-4 animate-spin" /> Voiding…</>
              : <><Icon name="trash-2" className="w-4 h-4" /> Void invoice</>}
          </button>
        </div>
      </div>
    </div>
  )
}

// Finalise a DRAFT: marks exactly the labels + adjustments recorded for it.
// Consequential billing action → explicit confirmation (CLAUDE.md §3).
function MarkInvoicedConfirmModal({
  inv, onConfirm, onCancel, isPending, error,
}: {
  inv: InvoiceListRow
  onConfirm: () => void
  onCancel: () => void
  isPending: boolean
  error: string | null
}) {
  const legacy = !inv.items_recorded
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={(e) => { if (e.target === e.currentTarget && !isPending) onCancel() }}
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 p-6">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-mint-50 flex items-center justify-center shrink-0">
            <Icon name="check-circle" className="w-5 h-5 text-mint-600" />
          </div>
          <div>
            <div className="text-[15px] font-semibold">Mark {inv.invoice_number} as invoiced?</div>
            <div className="text-[13px] text-ink-500 mt-1">
              Every label and adjustment on this draft is marked <span className="font-semibold">INVOICED</span> to {inv.invoice_number}.
              They will not appear on any later invoice. Do this only for the final version you send to the customer.
            </div>
          </div>
        </div>
        <div className="bg-ink-50 rounded-lg px-4 py-3 text-[12.5px] text-ink-600 mb-4 space-y-0.5">
          <div><span className="font-semibold">Customer:</span> {inv.customer_name}</div>
          <div><span className="font-semibold">Period:</span> {inv.period_start} → {inv.period_end}</div>
          <div><span className="font-semibold">Total:</span> {fmtUsd(inv.total)} · {inv.new_shipment_count} shipments · {inv.adjustment_count} adjustments</div>
        </div>
        {legacy && (
          <div className="mb-4 px-3 py-2 rounded-lg bg-honey-50 border border-honey-200 text-[12.5px] text-honey-700">
            This draft was generated before invoice contents were recorded, so it cannot be finalised.
            Void it and generate it again with “Mark invoiced” ticked.
          </div>
        )}
        {error && (
          <div className="mb-4 px-3 py-2 rounded-lg bg-flame-50 border border-flame-200 text-[12.5px] text-flame-700">{error}</div>
        )}
        <div className="flex items-center justify-end gap-3">
          <button onClick={onCancel} disabled={isPending}
            className="h-9 px-4 rounded-lg border border-ink-200 text-[13px] font-semibold hover:bg-ink-50 disabled:opacity-50">
            Cancel
          </button>
          <button onClick={onConfirm} disabled={isPending || legacy}
            className="h-9 px-4 rounded-lg bg-mint-600 text-white text-[13px] font-semibold hover:bg-mint-700 disabled:opacity-50 inline-flex items-center gap-2">
            {isPending
              ? <><Icon name="loader" className="w-4 h-4 animate-spin" /> Marking…</>
              : <><Icon name="check-circle" className="w-4 h-4" /> Mark as invoiced</>}
          </button>
        </div>
      </div>
    </div>
  )
}

export function InvoicesView() {
  const { t } = useTranslation()
  const qc = useQueryClient()

  const [customer, setCustomer] = useState('')
  const [customerLike, setCustomerLike] = useState(true)
  // Picker exposes an INCLUSIVE end-date (what the user sees & clicks).
  // The backend wants EXCLUSIVE period_end, so we add one day before sending.
  const [periodStart, setPeriodStart] = useState(monthAgoIso())
  const [periodEndInclusive, setPeriodEndInclusive] = useState(todayIso())
  const [invoiceDate, setInvoiceDate] = useState(todayIso())
  const [carrier, setCarrier] = useState('')
  const [billTo, setBillTo] = useState('')
  const [billToPhone, setBillToPhone] = useState('')
  const [markInvoiced, setMarkInvoiced] = useState(false)
  const [result, setResult] = useState<GenerateResult | null>(null)
  const [viewingInvoice, setViewingInvoice] = useState<InvoiceListRow | null>(null)
  const [voidTarget, setVoidTarget] = useState<InvoiceListRow | null>(null)
  const [markTarget, setMarkTarget] = useState<InvoiceListRow | null>(null)
  const [openMenuFor, setOpenMenuFor] = useState<string | null>(null)
  const [historyFor, setHistoryFor] = useState<string | null>(null)   // invoice number
  const { isAdmin } = useAuth()

  // --- Customer dropdown ----
  const customersQuery = useQuery<{ customers: CustomerOption[] }>({
    queryKey: ['invoices', 'customers'],
    queryFn: () => fetch('/api/invoices/customers').then(r => r.json()),
    staleTime: 5 * 60_000,
  })

  // --- Past invoices list ----
  const listQuery = useQuery<{ invoices: InvoiceListRow[] }>({
    queryKey: ['invoices', 'list'],
    queryFn: () => fetch('/api/invoices?limit=100').then(r => r.json()),
    staleTime: 10_000,
  })

  // --- Generate mutation ----
  // We don't throw on HTTP errors — we return the JSON payload either way so
  // the `tail` / `exit_code` fields from the API make it to the UI.
  const generate = useMutation<GenerateResult, Error, void>({
    mutationFn: async () => {
      const body = {
        customer,
        like: customerLike,
        period_start: periodStart,
        // API expects EXCLUSIVE period_end; picker UI uses INCLUSIVE end-date.
        period_end: addOneDay(periodEndInclusive),
        invoice_date: invoiceDate,
        carrier: carrier || null,
        bill_to: billTo || undefined,
        bill_to_phone: billToPhone || undefined,
        mark_invoiced: markInvoiced,
      }
      const r = await fetch('/api/invoices/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      const text = await r.text()
      let data: GenerateResult
      try { data = JSON.parse(text) as GenerateResult }
      catch { data = { ok: false, error: `HTTP ${r.status}`, tail: text.slice(0, 2000) } }
      if (!r.ok && !data.error) data.error = `HTTP ${r.status}`
      return data
    },
    onSuccess: (data) => {
      setResult(data)
      if (!data.error && data.invoice_number) {
        qc.invalidateQueries({ queryKey: ['invoices', 'list'] })
        // Auto-trigger downloads of BOTH files so the user always gets them.
        triggerDownload(`/api/invoices/${data.invoice_number}/xlsx`)
        // Small delay so the browser actually starts a second download instead
        // of replacing the first navigation.
        setTimeout(() => triggerDownload(`/api/invoices/${data.invoice_number}/pdf`), 400)
      }
    },
    onError: (e) => {
      setResult({ ok: false, error: e.message })
    },
  })

  // --- Void mutation ----
  const voidMutation = useMutation<{ ok: boolean; voided: string }, Error, string>({
    mutationFn: async (invoiceNumber) => {
      const r = await fetch(`/api/invoices/${invoiceNumber}/void`, { method: 'DELETE' })
      const data = await r.json()
      if (!r.ok) throw new Error(data.error || `HTTP ${r.status}`)
      return data
    },
    onSuccess: () => {
      setVoidTarget(null)
      qc.invalidateQueries({ queryKey: ['invoices', 'list'] })
    },
  })

  // --- Mark-as-invoiced mutation (finalise a draft) ----
  const markMutation = useMutation<{ ok: boolean }, Error, string>({
    mutationFn: async (invoiceNumber) => {
      const r = await fetch(`/api/invoices/${invoiceNumber}/mark-invoiced`, { method: 'POST' })
      const data = await r.json().catch(() => ({}))
      if (!r.ok || !data?.ok) throw new Error(data?.error || `HTTP ${r.status}`)
      return data
    },
    onSuccess: () => {
      setMarkTarget(null)
      qc.invalidateQueries({ queryKey: ['invoices', 'list'] })
    },
  })

  const sortedCustomers = useMemo(() => {
    const list = customersQuery.data?.customers ?? []
    return list
  }, [customersQuery.data])

  const ready = customer && periodStart && periodEndInclusive && periodEndInclusive >= periodStart

  return (
    <div>
      <div className="mb-8">
        <div className="text-[12px] font-semibold text-mint-600 uppercase tracking-wider">Module 7</div>
        <h1 className="text-[32px] font-bold tracking-tight mt-2">Customer Invoices</h1>
        <p className="text-ink-500 text-[14px] mt-2">
          Generate a customer invoice (XLSX backup + PDF) from carrier-billed shipments.
          Invoice numbers are auto-allocated (MMMYYNNNN).
        </p>
      </div>

      <Card className="p-5 mb-5">
        <div className="text-[15px] font-semibold mb-1">Generate invoice</div>
        <div className="text-[12px] text-ink-500 mb-4">
          Pick a customer + period. Carrier is optional — leave on “All carriers” to roll up everything.
        </div>

        <div className="grid grid-cols-3 gap-3">
          <Field label="Customer">
            <select
              className="mt-1 w-full h-9 px-3 border border-ink-200 rounded-lg text-[13.5px] bg-white"
              value={customer}
              onChange={(e) => setCustomer(e.target.value)}
            >
              <option value="">— select customer —</option>
              {sortedCustomers.map(c => (
                <option key={c.customer_name} value={c.customer_name}>
                  {c.customer_name} ({c.shipment_count.toLocaleString()})
                </option>
              ))}
            </select>
          </Field>

          <Field label="Carrier (optional)">
            <select
              className="mt-1 w-full h-9 px-3 border border-ink-200 rounded-lg text-[13.5px] bg-white"
              value={carrier}
              onChange={(e) => setCarrier(e.target.value)}
            >
              {CARRIERS.map(c => (
                <option key={c.code || 'all'} value={c.code}>{c.label}</option>
              ))}
            </select>
          </Field>

          <Field label="Invoice date">
            <input
              type="date"
              className="mt-1 w-full h-9 px-3 border border-ink-200 rounded-lg text-[13.5px] bg-white"
              value={invoiceDate}
              onChange={(e) => setInvoiceDate(e.target.value)}
            />
          </Field>

          <div className="col-span-2">
            <Field label="Period">
              <div className="mt-1">
                <DateRangePicker
                  value={{ from: periodStart, to: periodEndInclusive }}
                  onChange={({ from, to }) => {
                    setPeriodStart(from)
                    setPeriodEndInclusive(to)
                  }}
                />
              </div>
            </Field>
          </div>
          <Field label="Customer match">
            <label className="mt-1 flex items-center gap-2 text-[13px] h-9">
              <input
                type="checkbox"
                checked={customerLike}
                onChange={(e) => setCustomerLike(e.target.checked)}
              />
              Match as prefix (recommended)
            </label>
          </Field>

          <Field label="Bill-to address (optional, multi-line)">
            <textarea
              className="mt-1 w-full px-3 py-2 border border-ink-200 rounded-lg text-[13.5px] bg-white"
              rows={3}
              value={billTo}
              onChange={(e) => setBillTo(e.target.value)}
              placeholder={'Suite 305, 250 Moonachie Road\nMoonachie, 07074 NJ'}
            />
          </Field>
          <Field label="Bill-to phone (optional)">
            <input
              className="mt-1 w-full h-9 px-3 border border-ink-200 rounded-lg text-[13.5px] bg-white"
              value={billToPhone}
              onChange={(e) => setBillToPhone(e.target.value)}
              placeholder="+1 201 782 6083"
            />
          </Field>
          <Field label="On success">
            <label className="mt-1 flex items-center gap-2 text-[13px] h-9">
              <input
                type="checkbox"
                checked={markInvoiced}
                onChange={(e) => setMarkInvoiced(e.target.checked)}
              />
              Mark included rows as INVOICED
            </label>
          </Field>
        </div>

        <div className="flex items-center gap-3 pt-4 mt-2 border-t border-ink-100">
          <div className="text-[12.5px] text-ink-500">
            {customer ? `Customer: ${customer}` : 'Pick a customer to begin.'}
            {periodStart && periodEndInclusive ? ` · ${periodStart} → ${periodEndInclusive}` : ''}
          </div>
          <button
            disabled={!ready || generate.isPending}
            onClick={() => generate.mutate()}
            className="ml-auto h-9 px-4 rounded-lg bg-mint-500 text-white font-semibold inline-flex items-center gap-2 disabled:opacity-50"
          >
            {generate.isPending
              ? (<><Icon name="loader" className="animate-spin w-4 h-4" /> Generating…</>)
              : (<><Icon name="file-down" className="w-4 h-4" /> Generate invoice</>)}
          </button>
        </div>

        {result && (
          <div className={`mt-4 rounded-lg border p-3 text-[13px] ${result.error ? 'border-flame-200 bg-flame-50' : 'border-mint-200 bg-mint-50'}`}>
            {result.error ? (
              <>
                <div className="font-semibold text-flame-700">Generation failed</div>
                <div className="mt-1 text-flame-700">{result.error}</div>
                {result.exit_code !== undefined && (
                  <div className="mt-1 text-[11px] text-flame-700">exit code: {result.exit_code}</div>
                )}
                {result.traceback && (
                  <details className="mt-2" open>
                    <summary className="text-[11px] font-semibold text-flame-700 cursor-pointer">Python traceback</summary>
                    <pre className="mt-1 text-[11px] whitespace-pre-wrap text-flame-700 bg-white/60 rounded p-2 max-h-72 overflow-auto">{result.traceback}</pre>
                  </details>
                )}
                {result.tail && (
                  <details className="mt-2" open={!result.traceback}>
                    <summary className="text-[11px] font-semibold text-flame-700 cursor-pointer">stderr tail</summary>
                    <pre className="mt-1 text-[11px] whitespace-pre-wrap text-flame-700 bg-white/60 rounded p-2 max-h-72 overflow-auto">{result.tail}</pre>
                  </details>
                )}
              </>
            ) : (
              <div>
                <div className="font-semibold text-mint-700">
                  Invoice {result.invoice_number} · Total {fmtUsd(result.total)}
                </div>
                <div className="text-[12.5px] text-ink-600 mt-1">
                  {result.new_shipment_count} shipments
                  {' · '}{result.adjustment_count} adjustments
                  {result.marked_invoiced ? ' · marked INVOICED' : ' · advisory only'}
                </div>
                {result.carrier_totals && (
                  <div className="mt-2 grid grid-cols-3 gap-2">
                    {Object.entries(result.carrier_totals).map(([c, v]) => (
                      <div key={c} className="text-[12px] text-ink-700 flex items-center gap-2">
                        <span className="pill pill-ink">{c.toUpperCase()}</span>
                        <span className="font-semibold num">{fmtUsd(v)}</span>
                      </div>
                    ))}
                  </div>
                )}
                <div className="mt-3 text-[12px] text-ink-500">
                  Your downloads should start automatically. If your browser
                  blocked them, use the buttons below.
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <DownloadLink
                    href={`/api/invoices/${result.invoice_number}/xlsx`}
                    download
                    className="inline-flex items-center gap-2 h-9 px-4 rounded-lg bg-mint-500 text-white text-[13px] font-semibold hover:bg-mint-600"
                  >
                    <Icon name="file-spreadsheet" className="w-4 h-4" />
                    Download XLSX backup
                  </DownloadLink>
                  <DownloadLink
                    href={`/api/invoices/${result.invoice_number}/pdf`}
                    download
                    className="inline-flex items-center gap-2 h-9 px-4 rounded-lg bg-royal-500 text-white text-[13px] font-semibold hover:bg-royal-600"
                  >
                    <Icon name="file-text" className="w-4 h-4" />
                    Download PDF invoice
                  </DownloadLink>
                </div>
              </div>
            )}
          </div>
        )}
      </Card>

      <Card className="p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[15px] font-semibold">Recent invoices</div>
            <div className="text-[12px] text-ink-500 mt-0.5">Last 100 generated by this dashboard.</div>
          </div>
          <button
            onClick={() => qc.invalidateQueries({ queryKey: ['invoices', 'list'] })}
            className="h-8 px-3 rounded-lg text-[12.5px] font-semibold inline-flex items-center gap-1 hover:bg-ink-50"
          >
            <Icon name="refresh-cw" className="w-3.5 h-3.5" /> Refresh
          </button>
        </div>
        {listQuery.isLoading && <div className="text-[13px] text-ink-500">Loading…</div>}
        {listQuery.error && <div className="text-[13px] text-flame-600">Failed to load.</div>}
        {listQuery.data && (listQuery.data.invoices.length === 0
          ? <div className="text-[13px] text-ink-500">No invoices yet.</div>
          : (
            <div className="overflow-x-auto">
              <table className="w-full text-[13px]">
                <thead className="text-[11px] uppercase tracking-wider text-ink-500">
                  <tr className="border-b border-ink-100">
                    <th className="text-left py-2">Invoice #</th>
                    <th className="text-left py-2">Customer</th>
                    <th className="text-left py-2">Invoice date</th>
                    <th className="text-left py-2">Period</th>
                    <th className="text-left py-2">Carrier</th>
                    <th className="text-right py-2">Ship.</th>
                    <th className="text-right py-2">Adj.</th>
                    <th className="text-right py-2">Total</th>
                    <th className="text-left py-2">Status</th>
                    <th className="text-right py-2">Files</th>
                    <th className="py-2 w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {listQuery.data.invoices.map(inv => (
                    <tr key={inv.invoice_number} className="border-b border-ink-50 hover:bg-ink-50">
                      <td className="py-2 font-semibold mono">{inv.invoice_number}</td>
                      <td className="py-2">{inv.customer_name}</td>
                      <td className="py-2 num">{inv.invoice_date ? inv.invoice_date.slice(0, 10) : '—'}</td>
                      <td className="py-2 text-ink-500 num">{inv.period_start} → {inv.period_end}</td>
                      <td className="py-2 text-ink-600">
                        {(inv.carrier_filter || 'all').toUpperCase()}
                      </td>
                      <td className="py-2 text-right num">{inv.new_shipment_count}</td>
                      <td className="py-2 text-right num">{inv.adjustment_count}</td>
                      <td className="py-2 text-right num font-semibold">{fmtUsd(inv.total)}</td>
                      <td className="py-2">
                        {inv.marked_invoiced
                          ? <span className="pill pill-mint">INVOICED</span>
                          : <span className="pill pill-ink">DRAFT</span>}
                      </td>
                      <td className="py-2 text-right">
                        <button
                          onClick={() => setViewingInvoice(inv)}
                          className="mr-2 inline-flex items-center gap-1 text-ink-600 hover:text-ink-900 hover:underline"
                        >
                          <Icon name="eye" className="w-3.5 h-3.5" /> View
                        </button>
                        <DownloadLink href={`/api/invoices/${inv.invoice_number}/xlsx`} className="mr-1 inline-flex items-center gap-1 text-mint-700 hover:underline">
                          <Icon name="file-spreadsheet" className="w-3.5 h-3.5" /> XLSX
                        </DownloadLink>
                        <DownloadLink href={`/api/invoices/${inv.invoice_number}/pdf`} className="inline-flex items-center gap-1 text-royal-700 hover:underline">
                          <Icon name="file-text" className="w-3.5 h-3.5" /> PDF
                        </DownloadLink>
                      </td>
                      {/* ⋯ actions menu */}
                      <td className="py-2 text-right relative">
                        <button
                          onClick={() => setOpenMenuFor(
                            openMenuFor === inv.invoice_number ? null : inv.invoice_number
                          )}
                          className="h-7 w-7 rounded-md flex items-center justify-center hover:bg-ink-100 ml-auto"
                          title="More actions"
                        >
                          <Icon name="more-horizontal" className="w-4 h-4 text-ink-500" />
                        </button>
                        {openMenuFor === inv.invoice_number && (
                          <>
                            {/* click-away backdrop */}
                            <div
                              className="fixed inset-0 z-10"
                              onClick={() => setOpenMenuFor(null)}
                            />
                            <div className="absolute right-0 top-8 z-20 w-48 bg-white border border-ink-200 rounded-lg shadow-lg py-1 text-[13px]">
                              {!inv.marked_invoiced && (
                                <button
                                  onClick={() => {
                                    setOpenMenuFor(null)
                                    markMutation.reset()
                                    setMarkTarget(inv)
                                  }}
                                  className="w-full flex items-center gap-2 px-3 py-2 text-mint-700 hover:bg-mint-50"
                                >
                                  <Icon name="check-circle" className="w-3.5 h-3.5" />
                                  Mark as invoiced
                                </button>
                              )}
                              {isAdmin && (
                                <button
                                  onClick={() => { setOpenMenuFor(null); setHistoryFor(inv.invoice_number) }}
                                  className="w-full flex items-center gap-2 px-3 py-2 text-ink-700 hover:bg-ink-50"
                                >
                                  <Icon name="history" className="w-3.5 h-3.5" />
                                  History
                                </button>
                              )}
                              <button
                                onClick={() => {
                                  setOpenMenuFor(null)
                                  setVoidTarget(inv)
                                }}
                                className="w-full flex items-center gap-2 px-3 py-2 text-flame-600 hover:bg-flame-50"
                              >
                                <Icon name="trash-2" className="w-3.5 h-3.5" />
                                Void invoice
                              </button>
                            </div>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}
      </Card>

      {viewingInvoice && (
        <InvoiceDetailModal
          inv={viewingInvoice}
          onClose={() => setViewingInvoice(null)}
        />
      )}

      {markTarget && (
        <MarkInvoicedConfirmModal
          inv={markTarget}
          isPending={markMutation.isPending}
          error={markMutation.error ? markMutation.error.message : null}
          onConfirm={() => markMutation.mutate(markTarget.invoice_number)}
          onCancel={() => { setMarkTarget(null); markMutation.reset() }}
        />
      )}

      {historyFor && (
        <div className="fixed inset-0 z-50 bg-ink-900/30 flex items-center justify-center p-4" onClick={() => setHistoryFor(null)}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[80vh] overflow-y-auto p-5" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <div className="text-[14px] font-semibold text-ink-900">Invoice {historyFor}</div>
              <button onClick={() => setHistoryFor(null)} className="text-ink-400 hover:text-ink-700"><Icon name="x" className="w-4 h-4" /></button>
            </div>
            <RecordHistory entity="customer_invoices" id={historyFor} defaultOpen />
          </div>
        </div>
      )}

      {voidTarget && (
        <VoidConfirmModal
          inv={voidTarget}
          isPending={voidMutation.isPending}
          onConfirm={() => voidMutation.mutate(voidTarget.invoice_number)}
          onCancel={() => { setVoidTarget(null); voidMutation.reset() }}
        />
      )}
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="text-[12px] font-semibold text-ink-500 uppercase tracking-wider">{label}</label>
      {children}
    </div>
  )
}
